<template>
  <div class="space-y-4">
    <p v-if="!files.length && !uploads.length" class="text-sm text-gray-600">{{ $t('projects.file_empty') }}</p>

    <!-- Ficheros del bloque, en el orden en que los verá el alumno. -->
    <vue-draggable v-if="files.length" :model-value="files" @update:model-value="setFiles" tag="ol"
                   handle=".file-drag-handle" :animation="200" class="space-y-2"
                   :aria-label="$t('projects.file_list_label', {count: files.length})">
      <li v-for="(file, index) in files" :key="file.file_uuid" class="rounded-md border border-gray-200 bg-white p-3">
        <div class="flex items-start gap-2">
          <span class="file-drag-handle flex h-8 w-6 flex-shrink-0 items-center justify-center cursor-grab active:cursor-grabbing text-gray-500 hover:text-gray-700"
                :title="$t('projects.file_drag')" aria-hidden="true">
            <font-awesome-icon icon="grip-lines" class="h-4 w-4"/>
          </span>
          <font-awesome-icon icon="file-alt" class="mt-2 h-4 w-4 flex-shrink-0 text-gray-500" aria-hidden="true"/>

          <div class="min-w-0 flex-1">
            <p class="flex items-baseline gap-2 text-sm">
              <a v-if="file.url" :href="file.url" target="_blank" rel="noopener noreferrer"
                 class="truncate font-medium text-gray-900 underline-offset-2 hover:underline rounded-md focus:outline-none focus:ring-2 focus:ring-bpblue">
                {{ fileLabel(file) }}
                <span class="sr-only">({{ $t('projects.file_open_new_tab') }})</span>
              </a>
              <span v-else class="truncate font-medium text-gray-900">{{ fileLabel(file) }}</span>
              <span v-if="file.size" class="flex-shrink-0 text-xs text-gray-600">{{ fileSize(file.size) }}</span>
            </p>
            <p v-if="file.missing" class="mt-1 text-sm text-red-600">{{ $t('projects.file_missing') }}</p>

            <label :for="blockId + '-name-' + index" class="mt-2 block text-xs font-medium text-gray-700">
              {{ $t('projects.file_name_label') }}
            </label>
            <input :id="blockId + '-name-' + index" type="text" maxlength="255" :value="file.name ?? ''"
                   :placeholder="file.original_name ?? ''" @input="renameFile(index, $event.target.value)"
                   class="mt-1 appearance-none block w-full px-3 py-1.5 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-bpblue focus:border-bpblue sm:text-sm">
          </div>

          <div class="flex flex-shrink-0 items-center">
            <button type="button" v-on:click="moveFile(index, -1)" :disabled="index === 0"
                    :title="$t('projects.file_move_up')" :aria-label="$t('projects.file_move_up') + ': ' + fileLabel(file)"
                    class="flex h-8 w-8 items-center justify-center rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-30 focus:outline-none focus:ring-2 focus:ring-bpblue">
              <font-awesome-icon icon="chevron-up" class="h-3.5 w-3.5" aria-hidden="true"/>
            </button>
            <button type="button" v-on:click="moveFile(index, 1)" :disabled="index === files.length - 1"
                    :title="$t('projects.file_move_down')" :aria-label="$t('projects.file_move_down') + ': ' + fileLabel(file)"
                    class="flex h-8 w-8 items-center justify-center rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-30 focus:outline-none focus:ring-2 focus:ring-bpblue">
              <font-awesome-icon icon="chevron-down" class="h-3.5 w-3.5" aria-hidden="true"/>
            </button>
            <button type="button" v-on:click="removeFile(index)"
                    :title="$t('projects.file_remove')" :aria-label="$t('projects.file_remove') + ': ' + fileLabel(file)"
                    class="flex h-8 w-8 items-center justify-center rounded-md text-red-600 hover:text-red-400 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-bpblue">
              <font-awesome-icon icon="trash" class="h-3.5 w-3.5" aria-hidden="true"/>
            </button>
          </div>
        </div>
      </li>
    </vue-draggable>

    <!-- Subidas en curso, en cola o fallidas: no forman parte del bloque hasta que terminan. -->
    <ul v-if="uploads.length" class="space-y-2">
      <li v-for="upload in uploads" :key="upload.id" class="rounded-md border p-3"
          :class="upload.status === 'error' ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-gray-50'">
        <div class="flex items-start gap-2">
          <font-awesome-icon :icon="upload.status === 'error' ? 'exclamation-triangle' : 'spinner'"
                             class="mt-0.5 h-4 w-4 flex-shrink-0"
                             :class="upload.status === 'error' ? 'text-red-600' : 'text-gray-500 animate-spin motion-reduce:animate-none'"
                             aria-hidden="true"/>
          <div class="min-w-0 flex-1">
            <p class="truncate text-sm font-medium text-gray-900">{{ upload.name }}</p>
            <p v-if="upload.status === 'error'" class="text-sm text-red-600" role="alert">{{ upload.error }}</p>
            <template v-else>
              <p class="text-sm text-gray-700" role="status">
                {{ upload.status === 'uploading' ? $t('projects.file_uploading', {progress: upload.progress}) : $t('projects.file_queued') }}
              </p>
              <div class="mt-2 h-1.5 w-full overflow-hidden rounded-md bg-gray-200" aria-hidden="true">
                <div class="h-full bg-bpblue motion-safe:transition-all" :style="{width: upload.progress + '%'}"></div>
              </div>
            </template>
          </div>
          <template v-if="upload.status === 'error'">
            <button v-if="upload.file" type="button" v-on:click="retryUpload(upload)"
                    :title="$t('projects.file_retry')" :aria-label="$t('projects.file_retry') + ': ' + upload.name"
                    class="flex h-8 w-8 items-center justify-center rounded-md text-gray-600 hover:text-gray-800 hover:bg-white focus:outline-none focus:ring-2 focus:ring-bpblue">
              <font-awesome-icon icon="redo" class="h-3.5 w-3.5" aria-hidden="true"/>
            </button>
            <button type="button" v-on:click="dismissUpload(upload)"
                    :title="$t('projects.file_dismiss')" :aria-label="$t('projects.file_dismiss') + ': ' + upload.name"
                    class="flex h-8 w-8 items-center justify-center rounded-md text-gray-600 hover:text-gray-800 hover:bg-white focus:outline-none focus:ring-2 focus:ring-bpblue">
              <font-awesome-icon icon="times" class="h-3.5 w-3.5" aria-hidden="true"/>
            </button>
          </template>
        </div>
      </li>
    </ul>

    <div>
      <!-- Se abre desde el botón para tener foco visible y un nombre accesible claro. -->
      <input ref="fileInput" type="file" multiple :accept="accept" class="hidden" tabindex="-1" aria-hidden="true"
             @change="onFilesSelected">

      <div v-if="canAddMore" class="flex flex-col items-center rounded-md border-2 border-dashed px-4 py-6 text-center"
           :class="dragOver ? 'border-bpblue bg-blue-50' : 'border-gray-300'"
           @dragover.prevent="dragOver = true" @dragleave="dragOver = false" @drop.prevent="onDrop">
        <font-awesome-icon icon="file-upload" class="h-6 w-6 text-gray-500" aria-hidden="true"/>
        <button type="button" v-on:click="chooseFiles" :aria-describedby="blockId + '-files-help'"
                class="mt-2 inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-bpblue">
          {{ $t(files.length ? 'projects.file_add_more' : 'projects.file_choose') }}
        </button>
        <p class="mt-2 text-sm text-gray-600">{{ $t('projects.file_drop_hint') }}</p>
      </div>
      <p v-else class="text-sm text-gray-600" role="status">{{ $t('projects.file_limit_reached', {max: maxCount}) }}</p>

      <p :id="blockId + '-files-help'" class="mt-2 text-sm text-gray-600">
        {{ $t('projects.file_help', {max: maxMb, count: maxCount}) }}
      </p>
    </div>
  </div>
</template>

<script>
import { VueDraggable } from 'vue-draggable-plus';
import LessonBlockService from "@/services/project/LessonBlockService";
import {FILE_ACCEPT, FILE_MAX_COUNT, FILE_MAX_MB, fileUploadError, formatFileSize} from "@/views/projects/ProjectEdit/Blocks/fileRules";

let uploadCounter = 0

/*
 * Bloque de archivos descargables (key `file`, LMS-070). `data` es
 * { files: [{ file_uuid, name? }] }, de 1 a 20 ficheros en orden; `name` es la
 * etiqueta visible (vacía = nombre original). Cada fichero se sube al elegirlo
 * (POST …/lessons/{lesson}/files) y se añade al bloque al terminar.
 * `original_name`, `size`, `url` y `missing` son solo para el editor y no se envían.
 *
 * Las subidas van de una en una: cada una añade su fichero a partir del `data`
 * ya actualizado por la anterior, y no se dispara el límite de 30 por minuto.
 */
export default {
  name: "FileBlock",
  components: {
    VueDraggable
  },
  props: {
    modelValue: {
      type: Object,
      required: true
    },
    blockId: {
      type: String,
      required: true
    },
    lessonUuid: {
      type: String,
      required: true
    }
  },
  emits: ['update:modelValue'],
  data() {
    return {
      accept: FILE_ACCEPT,
      maxMb: FILE_MAX_MB,
      maxCount: FILE_MAX_COUNT,
      // { id, name, file, status: 'pending' | 'uploading' | 'error', progress, error }
      uploads: [],
      processing: false,
      dragOver: false,
    }
  },
  computed: {
    files() {
      return this.modelValue.files ?? []
    },
    pendingCount() {
      return this.uploads.filter((upload) => upload.status !== 'error').length
    },
    canAddMore() {
      return this.files.length + this.pendingCount < FILE_MAX_COUNT
    }
  },
  methods: {
    setFiles(files) {
      this.$emit('update:modelValue', {...this.modelValue, files})
    },
    fileLabel(file) {
      return (file.name ?? '').trim() || file.original_name || this.$t('projects.file_unnamed')
    },
    fileSize(bytes) {
      return formatFileSize(bytes, this.$i18n.locale)
    },
    renameFile(index, name) {
      this.setFiles(this.files.map((file, position) => position === index ? {...file, name} : file))
    },
    moveFile(index, offset) {
      const target = index + offset
      if (target < 0 || target >= this.files.length) return

      const files = this.files.slice()
      const [file] = files.splice(index, 1)
      files.splice(target, 0, file)
      this.setFiles(files)
    },
    removeFile(index) {
      this.setFiles(this.files.filter((file, position) => position !== index))
    },
    chooseFiles() {
      this.$refs.fileInput.click()
    },
    onFilesSelected(event) {
      const files = Array.from(event.target.files ?? [])
      // Se limpia para que elegir otra vez el mismo fichero vuelva a disparar `change`.
      event.target.value = ''
      this.enqueue(files)
    },
    onDrop(event) {
      this.dragOver = false
      this.enqueue(Array.from(event.dataTransfer?.files ?? []))
    },
    // Valida cada fichero antes de subirlo; los que no caben o no valen se quedan como error.
    enqueue(files) {
      files.forEach((file) => {
        uploadCounter += 1
        const upload = {id: 'upload-' + uploadCounter, name: file.name, file: null, status: 'pending', progress: 0, error: null}
        const errorKey = this.canAddMore ? fileUploadError(file) : 'projects.file_too_many'

        if (errorKey) {
          upload.status = 'error'
          upload.error = this.$t(errorKey, {max: errorKey === 'projects.file_too_many' ? FILE_MAX_COUNT : FILE_MAX_MB})
        } else {
          upload.file = file
        }
        this.uploads.push(upload)
      })
      this.processQueue()
    },
    async processQueue() {
      if (this.processing) return
      this.processing = true

      let upload
      while ((upload = this.uploads.find((candidate) => candidate.status === 'pending'))) {
        await this.upload(upload)
      }
      this.processing = false
    },
    async upload(upload) {
      upload.status = 'uploading'
      upload.progress = 0
      try {
        const response = await LessonBlockService.uploadFile(this.lessonUuid, upload.file, (progress) => {
          upload.progress = progress
        })
        const saved = response.data?.data ?? {}
        this.uploads = this.uploads.filter((candidate) => candidate.id !== upload.id)
        this.setFiles([...this.files, {
          file_uuid: saved.uuid,
          name: '',
          original_name: saved.name ?? upload.name,
          size: saved.size ?? upload.file.size,
          url: saved.url ?? null,
        }])
      } catch (error) {
        upload.status = 'error'
        upload.error = this.uploadErrorMessage(error)
        // Un 422 no cambia al reintentar: solo se puede quitar.
        if (error?.response?.status === 422) upload.file = null
      }
    },
    retryUpload(upload) {
      if (!upload.file) return
      if (!this.canAddMore) {
        upload.error = this.$t('projects.file_too_many', {max: FILE_MAX_COUNT})
        return
      }
      upload.status = 'pending'
      upload.error = null
      this.processQueue()
    },
    dismissUpload(upload) {
      this.uploads = this.uploads.filter((candidate) => candidate.id !== upload.id)
    },
    uploadErrorMessage(error) {
      const status = error?.response?.status
      if (status === 413) return this.$t('projects.file_too_large', {max: FILE_MAX_MB})
      if (status === 429) return this.$t('projects.file_upload_throttled')
      if (status === 422) return error.response.data?.errors?.file?.[0] ?? this.$t('projects.file_upload_error')
      return this.$t('projects.file_upload_error')
    }
  }
}
</script>
